import { Agent } from '@lucidtech/las-sdk-browser';
import { useCallback, useMemo, useState } from 'react';

import { DEFAULT_TEMPLATE, TEMPLATES } from '@/components/ui';
import { NewAgent } from '@/features/agents';
import { convertToFieldConfig } from '@/utils';

import { toAgentId, useCreateAgent, useDeleteAgent } from './api';
import { useAddExport } from './useAddExport';
import { useAddModel } from './useAddModel';
import { useAddTrigger } from './useAddTrigger';
import { useAddValidation } from './useAddValidation';
import { useAgent } from './useAgent.ts';
import { useDeleteExport } from './useDeleteExport.ts';
import { useDeleteModel } from './useDeleteModel.ts';
import { useDeleteTrigger } from './useDeleteTrigger.ts';
import { useDeleteValidation } from './useDeleteValidation.ts';

const prepareFieldConfigAndSamples = async (
  selectedTemplate: keyof typeof TEMPLATES | undefined,
  state: Partial<NewAgent> | undefined
) => {
  let fieldConfig = state?.fieldConfig ?? {};
  let templateSamples: File[] = [];
  const userProvidedSamples: File[] = state?.sampleDocuments ?? [];

  if (selectedTemplate && selectedTemplate !== DEFAULT_TEMPLATE) {
    fieldConfig = convertToFieldConfig(TEMPLATES[selectedTemplate]?.fields ?? []);

    templateSamples = await Promise.all(
      TEMPLATES[selectedTemplate]?.sampleDocuments?.map(async (sampleDocumentUri: string) => {
        const blob = await (await fetch(sampleDocumentUri)).blob();
        const name = decodeURI(sampleDocumentUri.split('/').at(-1) ?? 'sample');
        return new File([blob], name, { type: blob.type });
      })
    );
  }

  return { fieldConfig, templateSamples, userProvidedSamples };
};

type Status = {
  isSuccess: boolean;
  isPending: boolean;
};

export type ScaffoldAgentStatus = {
  model: Status;
  trigger: Status;
  export: Status;
  validation: Status;
  agent: Status;
  errors: Error[];
  isDone: boolean;
};

export const useScaffoldAgent = () => {
  const [errors, setErrors] = useState<Error[]>([]);
  const [agentId, setAgentId] = useState<string>();
  const [hasSelectedTrigger, setHasSelectedTrigger] = useState(false);
  const [hasSelectedExport, setHasSelectedExport] = useState(false);
  const { createRun } = useAgent();

  const { mutate: createAgent, isSuccess: isSuccessCreateAgent, isPending: isPendingCreateAgent } = useCreateAgent();
  const { addModel, isPending: isPendingAddModel, isSuccess: isSuccessAddModel } = useAddModel();
  const { addValidation, isPending: isPendingAddValidation, isSuccess: isSuccessAddValidation } = useAddValidation();
  const { addTrigger, isSuccess: isSuccessAddTrigger, isPending: isPendingAddTrigger } = useAddTrigger();
  const { addExport, isSuccess: isSuccessAddExport, isPending: isPendingAddExport } = useAddExport();

  const { mutateAsync: deleteAgent } = useDeleteAgent();
  const { deleteModel } = useDeleteModel();
  const { deleteValidation } = useDeleteValidation();
  const { deleteTrigger } = useDeleteTrigger();
  const { deleteExport } = useDeleteExport();

  const create = useCallback(
    (state?: Partial<NewAgent>): Partial<NewAgent> => {
      const selectedTemplate = state?.template;
      const selectedTrigger = state?.trigger;
      const selectedExport = state?.export;

      setHasSelectedTrigger(!!selectedTrigger);
      setHasSelectedExport(!!selectedExport);

      createAgent(
        { name: state?.agentName || selectedTemplate },
        {
          onError: (error) => {
            setErrors((errors) => [...errors, error]);
          },
          onSuccess: async (agent: Agent) => {
            const agentId = agent.agentId;

            const { fieldConfig, templateSamples, userProvidedSamples } = await prepareFieldConfigAndSamples(
              selectedTemplate,
              state
            );

            setAgentId(agent.agentId);

            const cleanupFns: (() => Promise<unknown>)[] = [];

            const onCleanup = async () => {
              /* cleanup in reverse order */
              for (const fn of cleanupFns.toReversed()) {
                try {
                  await fn();
                } catch (error) {
                  console.error(error);
                }
              }
            };

            cleanupFns.push(() => deleteAgent(toAgentId(agentId)));

            try {
              const model = await addModel({ agentId, fieldConfig });
              cleanupFns.push(() => deleteModel({ agentId, modelId: model.modelId }));

              if (model) {
                const validation = await addValidation({ agentId, modelId: model.modelId });
                cleanupFns.push(() => deleteValidation({ agentId, validationId: validation.validationId }));

                if (selectedTrigger) {
                  const triggerAction = await addTrigger({ agentId, triggerOption: selectedTrigger });
                  cleanupFns.push(() => deleteTrigger({ agentId, actionId: triggerAction.actionId }));
                }

                if (selectedExport) {
                  const exportAction = await addExport({ agentId, exportOption: selectedExport });
                  cleanupFns.push(() => deleteExport({ agentId, actionId: exportAction.actionId }));
                }
              }

              for (const sampleDocument of userProvidedSamples) {
                createRun({
                  file: sampleDocument,
                  agentId: agentId,
                  variables: { requireReview: { value: true } },
                }).catch(console.error);
              }

              for (const sampleDocument of templateSamples) {
                createRun({
                  file: sampleDocument,
                  agentId: agentId,
                  variables: { isSampleDocument: { value: true }, requireReview: { value: true } },
                }).catch(console.error);
              }
            } catch (error) {
              console.error(error);

              onCleanup().finally(() => {
                if (error instanceof Error) {
                  setErrors((errors) => [...errors, error]);
                } else {
                  setErrors((errors) => [...errors, new Error('An unknown error occurred')]);
                }
              });
            }
          },
        }
      );

      return state || {};
    },
    [
      addExport,
      addModel,
      addTrigger,
      addValidation,
      createAgent,
      createRun,
      deleteAgent,
      deleteExport,
      deleteModel,
      deleteTrigger,
      deleteValidation,
    ]
  );

  const isDone = useMemo(() => {
    const isDoneAddModel = isSuccessAddModel;
    const isDoneAddValidation = isSuccessAddValidation;
    const isDoneAddTrigger = !hasSelectedTrigger || isSuccessAddTrigger;
    const isDoneAddExport = !hasSelectedExport || isSuccessAddExport;
    return isDoneAddModel && isDoneAddValidation && isDoneAddTrigger && isDoneAddExport;
  }, [
    hasSelectedExport,
    hasSelectedTrigger,
    isSuccessAddExport,
    isSuccessAddModel,
    isSuccessAddTrigger,
    isSuccessAddValidation,
  ]);

  return {
    agentId,
    status: {
      isDone,
      model: { isSuccess: isSuccessAddModel, isPending: isPendingAddModel },
      export: { isPending: isPendingAddExport, isSuccess: isSuccessAddExport },
      trigger: { isPending: isPendingAddTrigger, isSuccess: isSuccessAddTrigger },
      validation: {
        isSuccess: isSuccessAddValidation,
        isPending: isPendingAddValidation,
      },
      agent: {
        isSuccess: isSuccessCreateAgent,
        isPending: isPendingCreateAgent,
      },
      errors,
    },
    hasSelectedTrigger,
    hasSelectedExport,
    create,
  };
};
